import { useState } from 'react';
import {
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { AddRounded as AddIcon } from '@material-ui/icons';
import Skeleton from '@material-ui/lab/Skeleton';

import { useAdminGetDeliveryPoints } from '../../hooks/admin-get-delivery-points.hook';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DeliveryPoint } from '../../types/delivery-point.type';

type DeliveryPointSearchTableProps = {
  selectedDeliveryPointIds: string[];
  onAddDeliveryPoint: (deliveryPoint: DeliveryPoint) => void;
  maxHeight?: string;
};

const useStyles = makeStyles({
  search: {
    marginBottom: '0.5rem',
  },
});

function DeliveryPointTableRow({
  deliveryPoint,
  isSelected,
  onAddDeliveryPoint,
}: {
  deliveryPoint: DeliveryPoint;
  isSelected: boolean;
  onAddDeliveryPoint: (deliveryPoint: DeliveryPoint) => void;
}) {
  return (
    <TableRow>
      <TableCell>{deliveryPoint.name}</TableCell>
      <TableCell>{deliveryPoint.address.city}</TableCell>
      <TableCell>
        <IconButton size="small" onClick={() => onAddDeliveryPoint(deliveryPoint)} disabled={isSelected}>
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

export function DeliveryPointSearchTable({
  selectedDeliveryPointIds,
  onAddDeliveryPoint,
  maxHeight = '40svh',
}: DeliveryPointSearchTableProps) {
  const classes = useStyles();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500).trim();
  const { isLoading, isRefetching, deliveryPoints } = useAdminGetDeliveryPoints({
    page: 1,
    limit: 10,
    sortBy: 'name',
    sortDirection: 'asc',
    filters: { isActive: true, ...(debouncedSearch ? { name: debouncedSearch } : {}) },
  });

  const isFetching = isLoading || isRefetching;
  const hasDeliveryPoints = deliveryPoints.length > 0;

  return (
    <>
      <TextField
        className={classes.search}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        size="small"
        label="Rechercher un point de livraison"
        placeholder="Nom du point de livraison"
        variant="outlined"
      />
      <Paper>
        <TableContainer style={{ minHeight: maxHeight, maxHeight }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Nom</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Ville</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Skeleton variant="rect" animation="wave" height={50} />
                  </TableCell>
                </TableRow>
              )}
              {!isFetching && !hasDeliveryPoints && (
                <TableRow>
                  <TableCell colSpan={3}>Aucun point de livraison</TableCell>
                </TableRow>
              )}
              {!isFetching &&
                hasDeliveryPoints &&
                deliveryPoints.map((deliveryPoint) => (
                  <DeliveryPointTableRow
                    key={deliveryPoint.id}
                    deliveryPoint={deliveryPoint}
                    isSelected={selectedDeliveryPointIds.includes(deliveryPoint.id)}
                    onAddDeliveryPoint={onAddDeliveryPoint}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}
